import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { resolveStorageUrl } from '../api/storageClient';
import './ProductCard.css';

export interface ProductSection {
  icon: string;
  label: string;
  items: string[];
}

export interface ProductSpecRow {
  label: string;
  value: string;
}

interface Props {
  name: string | undefined;
  badge?: string;
  sub?: string | null;
  description?: string;
  image?: string | null | undefined;
  imageAlt?: string;
  brand?: string;
  specRows?: ProductSpecRow[];
  sections?: ProductSection[];
  detailsHref?: string;
  reviewed?: boolean | null;
  endOfLife?: boolean | null;
  editMode: boolean;
  productId?: string;
  onRevisar?: (productId: string) => void;
}

function resolveImageUrl(image: string | null | undefined): string | null {
  if (!image) return null;
  return resolveStorageUrl(`/data/products/images/${image}`);
}

// Chevron-right icon for the specs link
const ChevronRight: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ProductCard: React.FC<Props> = ({
  name,
  badge,
  sub,
  description,
  image,
  imageAlt,
  brand,
  specRows,
  sections = [],
  detailsHref,
  reviewed,
  endOfLife,
  editMode,
  productId,
  onRevisar,
}) => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<string | null>(null);

  const handleChipClick = (label: string) => {
    setActiveSection(prev => (prev === label ? null : label));
  };

  const expanded = sections.find(s => s.label === activeSection);
  const imageUrl = resolveImageUrl(image);

  return (
    <article className="pc-card">

      {/* ── Image panel (top) ───────────────────────────── */}
      <div className="pc-image-panel">
        {imageUrl ? (
          <img
            className="pc-image"
            src={imageUrl}
            alt={imageAlt || name}
            loading="lazy"
          />
        ) : (
          <div className="pc-image-placeholder" aria-hidden="true">
            <svg viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="8" y="24" width="40" height="6" rx="3" fill="#cbd5e1"/>
              <rect x="16" y="16" width="24" height="8" rx="2.5" fill="#e2e8f0"/>
              <circle cx="12" cy="38" r="6" fill="#cbd5e1"/>
              <circle cx="44" cy="38" r="6" fill="#cbd5e1"/>
              <rect x="10" y="28" width="36" height="12" rx="3.5" fill="#e2e8f0"/>
            </svg>
          </div>
        )}

        {/* Badge overlaid on the image panel (top-right) */}
        {badge && <span className="pc-badge">{badge}</span>}

        {/* End-of-life badge (bottom-left) */}
        {endOfLife && <span className="pc-badge-eol">Descontinuado</span>}
      </div>

      {/* ── Content panel ───────────────────────────────── */}
      <div className="pc-body">

        {/* Brand */}
        {brand && (
          <div className="pc-meta">
            <span className="pc-brand">{brand}</span>
          </div>
        )}

        {/* Name + sub */}
        <h3 className="pc-name">{name}</h3>
        {sub && <p className="pc-sub">{sub}</p>}

        {/* Description */}
        {description && <p className="pc-description">{description}</p>}

        {/* Inline spec rows */}
        {specRows && specRows.length > 0 && (
          <dl className="pc-specrows">
            {specRows.map(row => (
              <div key={row.label} className="pc-specrow">
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {/* Section chips */}
        {sections.length > 0 && (
          <div className="pc-chips">
            {sections.map(section => (
              <button
                key={section.label}
                className={`pc-chip${activeSection === section.label ? ' pc-chip--active' : ''}`}
                onClick={() => handleChipClick(section.label)}
                aria-expanded={activeSection === section.label}
                title={section.label}
              >
                <span className="pc-chip-icon" aria-hidden="true">{section.icon}</span>
                <span>{section.label}</span>
                <span className="pc-chip-count">{section.items.length}</span>
              </button>
            ))}
          </div>
        )}

        {/* Footer actions */}
        <div className="pc-footer">
          {/* Edit-mode Revisar */}
          {onRevisar && editMode && productId ? (
            <button className="btn-revisar" onClick={() => onRevisar(productId)}>
              Editar
            </button>
          ) : (
            !reviewed && editMode && productId ? (
              <button
                className="btn-revisar"
                onClick={() => navigate(`/productos/revisar/${encodeURIComponent(productId)}`)}
              >
                Revisar
              </button>
            ) : (
              <span />
            )
          )}

          {/* Discrete specs link */}
          {detailsHref ? (
            <a href={detailsHref} className="pc-specs-link">
              Especificaciones
              <ChevronRight />
            </a>
          ) : (
            <span className="pc-specs-link pc-specs-link--disabled" aria-disabled="true">
              Especificaciones
              <ChevronRight />
            </span>
          )}
        </div>
      </div>

      {/* ── Expanded section drawer — full card width ── */}
      {expanded && (
        <div className="pc-drawer" role="region" aria-label={expanded.label}>
          <p className="pc-drawer-label">{expanded.label}</p>
          <ul className="pc-drawer-list">
            {expanded.items.map((item, i) => (
              <li key={i}>
                <svg className="pc-drawer-check" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <circle cx="8" cy="8" r="7" stroke="#c43f02" strokeOpacity="0.25" strokeWidth="1"/>
                  <path d="M5 8.2l2 2 4-4.2" stroke="#c43f02" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}

    </article>
  );
};

export const ProductCardSkeleton: React.FC = () => (
  <div className="pc-skeleton" aria-hidden="true">
    <div className="pc-skeleton-image" />
    <div className="pc-skeleton-body">
      <div className="pc-skeleton-bar pc-skeleton-bar--brand" />
      <div className="pc-skeleton-bar pc-skeleton-bar--name" />
      <div className="pc-skeleton-bar pc-skeleton-bar--sub" />
      <div className="pc-skeleton-bar pc-skeleton-bar--desc1" />
      <div className="pc-skeleton-bar pc-skeleton-bar--desc2" />
      <div className="pc-skeleton-bar pc-skeleton-bar--desc3" />
      <div className="pc-skeleton-chips">
        <div className="pc-skeleton-chip" />
        <div className="pc-skeleton-chip" />
        <div className="pc-skeleton-chip" />
      </div>
      <div className="pc-skeleton-footer">
        <div className="pc-skeleton-bar pc-skeleton-bar--action" />
        <div className="pc-skeleton-bar pc-skeleton-bar--link" />
      </div>
    </div>
  </div>
);

export default ProductCard;
